import { useId, useMemo, useRef, useState } from "react";
import { Icon, type IconName } from "./Icon.js";

/**
 * Pick a location or a category, or make one without leaving the screen.
 *
 * This is the control the capture flow lives or dies on. In a real basement the
 * next item is almost always in the same bin as the last one, so the fastest
 * path has to be **one tap on a recent** -- those are the chips above the box,
 * and they are there before anything is typed. After that comes typing two
 * letters and pressing Enter, and only then making a new one, which is still on
 * this screen because sending somebody to a settings page mid-sitting is how a
 * capture session ends.
 *
 * It is not a `<datalist>`: that gives typeahead for free and nothing else --
 * no recents, no "make this one", no control over what matching means.
 */

export interface Option {
  id: string;
  /** What the person reads: a category's name, or a location's whole path. */
  label: string;
}

export function Picker({
  label,
  icon,
  value,
  options,
  recents,
  onChange,
  onCreate,
  placeholder,
  /**
   * Somewhere a new one could go *inside*, offered as a second create button.
   * The capture screen passes the location the last item went into, because
   * "Bin 4, inside the shelf I was just filling" is the shape of nearly every
   * location made during a sitting.
   */
  inside,
}: {
  label: string;
  /** Shown beside the chosen value, so a location and a category read differently at a glance. */
  icon?: IconName;
  value: string | null;
  options: Option[];
  recents: Option[];
  onChange: (id: string | null) => void;
  /** `inside` is null for a top-level thing. Answers with the new id, or null if it could not. */
  onCreate: (name: string, inside: string | null) => Promise<string | null>;
  placeholder?: string;
  inside?: { id: string; label: string } | null;
}) {
  const id = useId();
  const [typed, setTyped] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const chosen = options.find((option) => option.id === value) ?? null;

  const matches = useMemo(() => {
    const needle = typed.trim().toLowerCase();
    if (!needle) return options.slice(0, 8);
    // Anything containing the words, rather than a prefix: "bin 3" should find
    // "Basement › Shelf A › Bin 3", which starts with neither word.
    const words = needle.split(/\s+/);
    return options
      .filter((option) => {
        const haystack = option.label.toLowerCase();
        return words.every((word) => haystack.includes(word));
      })
      .slice(0, 8);
  }, [options, typed]);

  const name = typed.trim();
  const exists = options.some((option) => option.label.toLowerCase() === name.toLowerCase());
  const canCreate = name.length > 0 && !exists;

  async function create(inside: string | null) {
    setBusy(true);
    try {
      const created = await onCreate(name, inside);
      if (created) {
        onChange(created);
        setTyped("");
        setOpen(false);
      }
    } finally {
      setBusy(false);
    }
  }

  function keyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setHighlight((current) => {
        const next = event.key === "ArrowDown" ? current + 1 : current - 1;
        return Math.max(0, Math.min(next, matches.length - 1));
      });
      return;
    }
    if (event.key === "Enter") {
      // Enter inside a form would submit it, and this control is inside the
      // capture form -- so a keyboard pick must not also save the item.
      event.preventDefault();
      const pick = matches[highlight];
      if (pick) {
        onChange(pick.id);
        setTyped("");
        setOpen(false);
      } else if (canCreate) {
        void create(null);
      }
      return;
    }
    if (event.key === "Escape") {
      setOpen(false);
      setTyped("");
    }
  }

  return (
    <div className="picker">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>

      {recents.length > 0 && (
        <div className="chips" aria-label={`Recent ${label.toLowerCase()}`}>
          {recents.map((option) => (
            <button
              key={option.id}
              type="button"
              className={option.id === value ? "chip chip-on" : "chip"}
              onClick={() => onChange(option.id === value ? null : option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {chosen ? (
        <div className="picker-chosen">
          {icon && <Icon name={icon} size={16} />}
          <span className="picker-value">{chosen.label}</span>
          <button
            type="button"
            className="button button-ghost button-small"
            onClick={() => {
              onChange(null);
              inputRef.current?.focus();
            }}
          >
            Change
          </button>
        </div>
      ) : (
        <div className="picker-box">
          <input
            id={id}
            ref={inputRef}
            className="input"
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            autoComplete="off"
            placeholder={placeholder}
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
              setHighlight(0);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            // A blur that fires before the click on an option would close the
            // list out from under the finger, so the close waits a tick.
            onBlur={() => window.setTimeout(() => setOpen(false), 150)}
            onKeyDown={keyDown}
          />

          {open && (matches.length > 0 || canCreate) && (
            <ul className="picker-list" role="listbox" aria-label={label}>
              {matches.map((option, index) => (
                <li key={option.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === highlight}
                    className={index === highlight ? "picker-option picker-option-on" : "picker-option"}
                    onMouseEnter={() => setHighlight(index)}
                    onClick={() => {
                      onChange(option.id);
                      setTyped("");
                      setOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                </li>
              ))}

              {canCreate && (
                <li>
                  <button
                    type="button"
                    className="picker-option picker-create"
                    disabled={busy}
                    onClick={() => void create(null)}
                  >
                    <Icon name="plus" size={16} />
                    {`Add “${name}”`}
                  </button>
                </li>
              )}

              {canCreate && inside && (
                <li>
                  <button
                    type="button"
                    className="picker-option picker-create"
                    disabled={busy}
                    onClick={() => void create(inside.id)}
                  >
                    <Icon name="plus" size={16} />
                    {`Add “${name}” inside ${inside.label}`}
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
